import { useCallback, useEffect, useRef, useState } from 'react'
import { getScreenerData, type ScreenerReadScope } from '@/lib/api'
import type { ScreenerOrderbookSource, ScreenerTickerData } from '@/types'

export function orderbookReadScope(sourceIds: string[]): ScreenerReadScope {
  const pairs = sourceIds.map(value => value.split(':'))
  return {
    metrics: [], markets: [], limit: 50,
    orderbookExchanges: [...new Set(pairs.map(([exchange]) => exchange))].sort(),
    orderbookMarkets: [...new Set(pairs.map(([, market]) => market))].sort(),
  }
}

// The strict source TTL is 30s. Apply it conservatively to every fallback
// carry, even venues without a server TTL. KRW venues also require an FX
// timestamp which this response does not expose, so never carry those books.
const FALLBACK_MAX_AGE_SECONDS = 30
export function freshFallbackBooks(sources: ScreenerOrderbookSource[], now = Date.now() / 1000) {
  return sources.filter(source => !['upbit', 'bithumb'].includes(source.exchange.toLowerCase())
    && Number.isFinite(source.updated_at) && Number(source.updated_at) > now - FALLBACK_MAX_AGE_SECONDS
    && Number(source.updated_at) <= now + 5)
}
export function mergeFallbackBooks(stored: ScreenerTickerData, retained: ScreenerOrderbookSource[], now = Date.now() / 1000) {
  const sources = new Map((stored.orderbook_sources || []).map(source => [source.id, source]))
  for (const source of freshFallbackBooks(retained, now)) {
    const current = sources.get(source.id)
    if (!current || Number(source.updated_at) > Number(current.updated_at ?? 0)) sources.set(source.id, source)
  }
  return { ...stored, orderbook_sources: [...sources.values()], available_exchanges: [...new Set([
    ...stored.available_exchanges, ...[...sources.values()].map(source => source.exchange),
  ])] }
}

/** The orderbook page owns its polling; no chart history, settings or ticker writes. */
export function useOrderbooksScreenerData(account: string, symbol: string, sourceIds: string[], enabled: boolean) {
  const context = JSON.stringify([account, symbol, [...sourceIds].sort()])
  const [request, setRequest] = useState({ sequence: 0, fallback: false, context })
  const [state, setState] = useState<{ context: string; data: ScreenerTickerData | null; loading: boolean;
    error: string | null; updatedAt: Date | null }>({ context, data: null, loading: false, error: null, updatedAt: null })
  const sequence = useRef(0)
  const cache = useRef<{ context: string; stored: ScreenerTickerData | null; fallback: ScreenerOrderbookSource[]; transientUntil: number; lastFallbackAt: number }>({
    context, stored: null, fallback: [], transientUntil: 0, lastFallbackAt: -Infinity,
  })
  const refresh = useCallback((fallback = false) => setRequest(current => ({ sequence: current.sequence + 1, fallback, context })), [context])

  useEffect(() => {
    const current = ++sequence.current
    let disposed = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let controller: AbortController | undefined
    let deadline: ReturnType<typeof setTimeout> | undefined
    let first = true
    let expiry: ReturnType<typeof setTimeout> | undefined
    if (cache.current.context !== context) cache.current = { context, stored: null, fallback: [], transientUntil: 0, lastFallbackAt: -Infinity }
    const [, ticker, sources] = JSON.parse(context) as [string, string, string[]]
    const isCurrent = () => !disposed && sequence.current === current
    setState(previous => ({ context, data: previous.context === context ? previous.data : null,
      updatedAt: previous.context === context ? previous.updatedAt : null, loading: false, error: null }))
    const scheduleExpiry = () => {
      clearTimeout(expiry)
      cache.current.fallback = freshFallbackBooks(cache.current.fallback)
      const earliest = Math.min(...cache.current.fallback.map(source => Number(source.updated_at) * 1000 + 30_000),
        cache.current.transientUntil || Infinity)
      if (!Number.isFinite(earliest)) return
      expiry = setTimeout(() => {
        if (!isCurrent()) return
        cache.current.fallback = freshFallbackBooks(cache.current.fallback)
        cache.current.transientUntil = 0
        if (cache.current.stored) setState(previous => ({ ...previous,
          data: mergeFallbackBooks(cache.current.stored!, cache.current.fallback) }))
        scheduleExpiry()
      }, Math.max(1, earliest - Date.now() + 1))
    }
    scheduleExpiry()
    const load = async (automaticFallback = false) => {
      if (!isCurrent() || !enabled || !ticker || !sources.length || document.hidden) return
      controller = new AbortController()
      const signal = controller.signal
      let timedOut = false
      deadline = setTimeout(() => { timedOut = true; controller?.abort() }, 10_000)
      const includeFallback = automaticFallback || (first && request.context === context && request.fallback)
      if (includeFallback) cache.current.lastFallbackAt = Date.now()
      let fetchMissingNext = false
      first = false
      setState(previous => ({ ...previous, loading: true }))
      try {
        const result = await getScreenerData(ticker, { mode: 'live' }, '1m', true, signal, [], undefined, false,
          { ...orderbookReadScope(sources), includeFallback })
        if (!isCurrent() || signal.aborted) return
        if (!result.ok || !result.data) throw new Error(result.error || 'Orderbooks are unavailable. Retrying automatically.')
        if (includeFallback) {
          cache.current.fallback = freshFallbackBooks(result.data.orderbook_sources || [])
          cache.current.stored ??= { ...result.data, orderbook_sources: [] }
          // Show the freshly server-validated response immediately, including
          // KRW books. They are not carried into the next stored snapshot and
          // this timer bounds their display even if that snapshot fails.
          cache.current.transientUntil = Date.now() + 5000
        } else {
          cache.current.stored = result.data
          cache.current.transientUntil = 0
          const present = new Set((result.data.orderbook_sources || []).filter(book => book.bids?.length || book.asks?.length).map(book => book.id))
          fetchMissingNext = sources.some(id => !present.has(id)) && Date.now() - cache.current.lastFallbackAt >= 10_000
        }
        const data = mergeFallbackBooks(includeFallback ? result.data : cache.current.stored, cache.current.fallback)
        setState({ context, data, loading: false, error: null, updatedAt: new Date() })
        scheduleExpiry()
      } catch (error) {
        if (!isCurrent()) return
        if (signal.aborted && !timedOut) return
        setState(previous => ({ ...previous, loading: false, error: timedOut
          ? 'Orderbook request timed out. Retrying automatically.'
          : error instanceof Error ? error.message : 'Could not load orderbooks. Retrying automatically.' }))
      } finally {
        clearTimeout(deadline)
        controller = undefined
        if (isCurrent()) setState(previous => ({ ...previous, loading: false }))
        if (isCurrent() && !document.hidden) timer = setTimeout(() => { void load(fetchMissingNext) }, fetchMissingNext ? 0 : 5000)
      }
    }
    const visibility = () => {
      clearTimeout(timer)
      if (document.hidden) controller?.abort()
      else if (!controller) void load()
    }
    void load()
    document.addEventListener('visibilitychange', visibility)
    return () => {
      disposed = true
      clearTimeout(timer)
      clearTimeout(deadline)
      clearTimeout(expiry)
      controller?.abort()
      document.removeEventListener('visibilitychange', visibility)
    }
  }, [context, enabled, request])

  return { ...(state.context === context ? state : { data: null, loading: enabled, error: null, updatedAt: null }), refresh }
}
